import { useTranslation } from 'react-i18next';
import {
  Bell,
  Check,
  ClipboardList,
  CreditCard,
  Globe,
  ListOrdered,
  Minus,
  Repeat,
  Shield,
  Smartphone,
  Users,
  X,
  type LucideIcon,
} from 'lucide-react';
import { LandingLayout } from '@/pages/landing/shared/LandingLayout';
import { SEOHead } from '@/pages/landing/shared/SEOHead';
import { useLandingAnalytics } from '@/hooks/useLandingAnalytics';
import { useDetectedCurrency } from '@/hooks/useCurrency';
import { cn } from '@/lib/utils';
import { LandingFaq } from '@/pages/landing/invite-shared/LandingFaq';
import { LocalizedScreenshot, PhoneFrame } from '@/pages/landing/invite-shared/ProductShots';
import { PricingBand, TrialButtons } from '@/pages/landing/invite-shared/TrialCta';
import { ProofBand } from '@/pages/landing/invite-shared/ProofBand';
import {
  Eyebrow,
  IconTile,
  Section,
  SectionTitle,
  cardClass,
  landingGradientStyles,
  type Item,
} from '@/pages/landing/invite-shared/layout';

const NS = 'landing-trainers';
const URL = 'https://slotyapp.com/for-trainers';

const TRUST_ICONS: LucideIcon[] = [CreditCard, Shield, Globe];
const FEATURE_ICONS: LucideIcon[] = [Users, ListOrdered, Smartphone, Bell, Repeat, ClipboardList];

// Comparison: messages & DMs / shared calendar / Sloty, per row (y = yes, n = no, p = partly).
const COMPARISON: ('y' | 'n' | 'p')[][] = [
  ['n', 'n', 'y'],
  ['n', 'p', 'y'],
  ['n', 'n', 'y'],
  ['n', 'n', 'y'],
  ['n', 'n', 'y'],
  ['n', 'y', 'y'],
];

/**
 * /for-trainers — personal trainers and small studios.
 * Design: Claude Design canvas "Gyms & Studios Landing" (improved /for-trainers) on the Sloty
 * design system. Standalone on purpose: the other verticals still use LandingPage.tsx.
 */
export default function TrainersLandingPage() {
  const { t } = useTranslation(NS);
  useLandingAnalytics('for-trainers');
  const { getStartingPrice } = useDetectedCurrency();
  const faq = t('faq.items', { returnObjects: true }) as { question: string; answer: string }[];

  return (
    <>
      <SEOHead
        title={t('seo.title')}
        description={t('seo.description')}
        canonical={URL}
        ogImage="https://slotyapp.com/images/og-trainers.jpg"
        structuredData={[
          { '@context': 'https://schema.org', '@type': 'WebPage', name: t('seo.title'), description: t('seo.description'), url: URL },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faq.map((f) => ({ '@type': 'Question', name: f.question, acceptedAnswer: { '@type': 'Answer', text: f.answer } })),
          },
        ]}
      />
      <LandingLayout>
        <div className="lp-trainers">
          <style>{landingGradientStyles}</style>
          <Hero />
          <ProofBand ns={NS} />
          <BeforeAfter />
          <Features />
          <Comparison />
          <PricingBand
            className="bg-surface"
            eyebrow={t('pricing.eyebrow')}
            title={t('pricing.title')}
            subtitle={t('pricing.subtitle').replace('{{startingPrice}}', getStartingPrice())}
            cta={t('pricing.cta')}
          />
          <LandingFaq ns={NS} />
          <FinalCta />
        </div>
      </LandingLayout>
    </>
  );
}

function Hero() {
  const { t } = useTranslation(NS);
  const trust = t('trust', { returnObjects: true }) as string[];
  return (
    <section className="lp-hero-fade px-5 pb-14 pt-8 sm:px-8 lg:px-20 lg:pb-[104px] lg:pt-[72px]">
      <div className="mx-auto grid w-full max-w-[1280px] items-center gap-10 lg:grid-cols-12 lg:gap-6">
        <div className="flex flex-col gap-5 lg:col-span-6 lg:gap-6">
          <Eyebrow>{t('hero.eyebrow')}</Eyebrow>
          <h1 className="m-0 text-balance font-display text-[36px] font-semibold leading-[1.1] tracking-[-0.025em] text-ink lg:text-[60px]">
            {t('hero.title')}
          </h1>
          <p className="m-0 max-w-[540px] text-base leading-[1.6] text-soft lg:text-lg">{t('hero.subtitle')}</p>
          <div className="flex flex-col gap-3">
            <TrialButtons cta={t('hero.cta')} secondary={t('hero.secondary')} />
            <p className="m-0 text-[13px] text-soft">{t('hero.note')}</p>
          </div>
          <ul className="m-0 flex list-none flex-col gap-3 p-0 text-[13px] font-medium text-soft sm:flex-row sm:flex-wrap sm:gap-x-6">
            {trust.map((item, i) => {
              const Icon = TRUST_ICONS[i];
              return (
                <li key={item} className="flex items-center gap-2">
                  <Icon className="h-4 w-4 shrink-0 text-brand-500" strokeWidth={1.75} aria-hidden="true" />
                  <span>{item}</span>
                </li>
              );
            })}
          </ul>
        </div>
        <div className="flex justify-center gap-5 lg:col-span-5 lg:col-start-8 lg:justify-end">
          <PhoneFrame className="w-[46%] max-w-[260px]">
            <LocalizedScreenshot set="gyms" name="booking-times" alt={t('screens.booking')} sizes="260px" />
          </PhoneFrame>
          <PhoneFrame className="mt-12 w-[46%] max-w-[260px]">
            <LocalizedScreenshot set="gyms" name="my-bookings" alt={t('screens.myBookings')} sizes="260px" />
          </PhoneFrame>
        </div>
      </div>
    </section>
  );
}

function Mark({ tone }: { tone: 'yes' | 'no' }) {
  return tone === 'yes' ? (
    <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-success-soft text-success-text">
      <Check className="h-3.5 w-3.5" strokeWidth={3} aria-hidden="true" />
    </span>
  ) : (
    <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-danger-soft text-danger-text">
      <X className="h-3.5 w-3.5" strokeWidth={3} aria-hidden="true" />
    </span>
  );
}

function BeforeAfter() {
  const { t } = useTranslation(NS);
  const before = t('beforeAfter.beforeItems', { returnObjects: true }) as string[];
  const after = t('beforeAfter.afterItems', { returnObjects: true }) as string[];
  const list = (items: string[], tone: 'yes' | 'no') => (
    <ul className="m-0 flex list-none flex-col gap-3.5 p-0">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-3 text-base leading-[1.5] text-ink lg:text-[17px]">
          <Mark tone={tone} />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
  return (
    <Section className="bg-surface">
      <SectionTitle className="mb-10 max-w-[820px] lg:mx-auto lg:mb-14 lg:text-center">{t('beforeAfter.title')}</SectionTitle>
      <div className="grid gap-4 md:grid-cols-2 lg:gap-6">
        <div className="flex flex-col gap-5 rounded-4xl border border-line bg-surface p-6 lg:p-9">
          <h3 className="m-0 font-display text-xl font-semibold text-soft">{t('beforeAfter.before')}</h3>
          {list(before, 'no')}
        </div>
        <div className="flex flex-col gap-5 rounded-4xl border border-brand-200 bg-brand-50 p-6 lg:p-9">
          <h3 className="m-0 font-display text-xl font-semibold text-brand-700">{t('beforeAfter.after')}</h3>
          {list(after, 'yes')}
        </div>
      </div>
    </Section>
  );
}

function Features() {
  const { t } = useTranslation(NS);
  const items = t('features.items', { returnObjects: true }) as Item[];
  return (
    <Section id="features" className="bg-brand-50">
      <div className="mb-10 flex flex-col gap-4 lg:mb-14 lg:items-center lg:text-center">
        <SectionTitle className="max-w-[820px]">{t('features.title')}</SectionTitle>
        <p className="m-0 text-base leading-[1.6] text-soft lg:text-lg">{t('features.subtitle')}</p>
      </div>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 lg:gap-6">
        {items.map((item, i) => (
          <div key={item.title} className={cn(cardClass, 'flex flex-col gap-4 p-6 lg:p-8')}>
            <IconTile icon={FEATURE_ICONS[i]} size="lg" />
            <h3 className="m-0 font-display text-[21px] font-semibold tracking-[-0.02em] text-ink">{item.title}</h3>
            <p className="m-0 text-[15px] leading-[1.6] text-soft lg:text-base">{item.description}</p>
          </div>
        ))}
      </div>
    </Section>
  );
}

function ComparisonMark({ value }: { value: 'y' | 'n' | 'p' }) {
  const { t } = useTranslation(NS);
  const config = {
    y: { Icon: Check, cls: 'bg-success-soft text-success-text', label: t('comparison.yes') },
    n: { Icon: X, cls: 'bg-surface-2 text-text-muted', label: t('comparison.no') },
    p: { Icon: Minus, cls: 'bg-warn-soft text-warn-text', label: t('comparison.partly') },
  }[value];
  return (
    <span className={cn('inline-flex h-7 w-7 items-center justify-center rounded-full', config.cls)} role="img" aria-label={config.label}>
      <config.Icon className="h-4 w-4" strokeWidth={2.75} aria-hidden="true" />
    </span>
  );
}

function Comparison() {
  const { t } = useTranslation(NS);
  const columns = t('comparison.columns', { returnObjects: true }) as string[];
  const rows = t('comparison.rows', { returnObjects: true }) as string[];
  return (
    <Section className="bg-bg-public">
      <SectionTitle className="mb-10 lg:mb-12 lg:text-center">{t('comparison.title')}</SectionTitle>

      {/* Desktop: table, the Sloty column highlighted. */}
      <div className={cn(cardClass, 'mx-auto hidden max-w-[980px] overflow-hidden md:block')}>
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="border-b border-line">
              <th scope="col" className="px-7 py-4"><span className="sr-only">{t('comparison.title')}</span></th>
              {columns.map((c, i) => (
                <th key={c} scope="col" className={cn('px-4 py-4 text-center text-[15px] font-semibold', i === 2 ? 'bg-brand-50 text-brand-700' : 'text-soft')}>
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, r) => (
              <tr key={row} className="border-b border-line last:border-0">
                <th scope="row" className="px-7 py-4 text-base font-normal text-ink">{row}</th>
                {COMPARISON[r].map((v, i) => (
                  <td key={i} className={cn('px-4 py-4 text-center', i === 2 && 'bg-brand-50')}>
                    <ComparisonMark value={v} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Phones: one card per row. */}
      <ul className="m-0 flex list-none flex-col gap-3 p-0 md:hidden">
        {rows.map((row, r) => (
          <li key={row} className="flex flex-col gap-4 rounded-2xl border border-line bg-surface p-4">
            <span className="text-[15.5px] font-semibold text-ink">{row}</span>
            <div className="grid grid-cols-3 gap-2">
              {COMPARISON[r].map((v, i) => (
                <div key={i} className="flex flex-col items-center gap-1.5 text-center">
                  <ComparisonMark value={v} />
                  <span className={cn('text-[11.5px] font-semibold', i === 2 ? 'text-brand-700' : 'text-soft')}>{columns[i]}</span>
                </div>
              ))}
            </div>
          </li>
        ))}
      </ul>
    </Section>
  );
}

function FinalCta() {
  const { t } = useTranslation(NS);
  return (
    <Section className="bg-bg-public">
      <div className="lp-final-panel flex flex-col items-stretch gap-8 rounded-[28px] px-[22px] py-8 lg:items-center lg:p-16">
        <div className="flex flex-col gap-4 lg:items-center lg:text-center">
          <SectionTitle>{t('final.title')}</SectionTitle>
          <p className="m-0 max-w-[560px] text-base leading-[1.6] text-soft lg:text-lg">{t('final.subtitle')}</p>
        </div>
        <TrialButtons cta={t('final.cta')} />
      </div>
    </Section>
  );
}
